import {
  type ChangeEvent,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  useEffect,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { InputRoot } from "./InputRoot";
import { generatePlaceholder } from "./utils/generatePlaceholder";
import { numberInputOnWheelPreventChange } from "./utils/numberInputWheel";

type InputNumberProps = InputHTMLAttributes<HTMLInputElement> & {
  fractionDigits?: number;
  allowZero?: boolean;
};

const toText = (value: InputNumberProps["value"]): string =>
  value == null || value === "" ? "" : String(value);

const formatToFractionDigits = (
  raw: string,
  fractionDigits: number,
): string => {
  const multiplier = Number(`1${"0".repeat(fractionDigits)}`);
  return (Math.floor(parseFloat(raw) * multiplier) / multiplier).toFixed(
    fractionDigits,
  );
};

export const InputNumber = forwardRef(
  (
    {
      fractionDigits,
      allowZero: _allowZero,
      onChange,
      onFocus,
      onBlur,
      value,
      ...props
    }: InputNumberProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    if (fractionDigits !== undefined && fractionDigits < 0) {
      throw new Error("fractionDigits cannot be less than 0");
    }

    // When fraction-digit rounding is requested, the field is normally
    // controlled by a numeric `value` prop that can't represent every state
    // needed mid-edit — an empty field, a trailing decimal point — without
    // becoming "" or NaN upstream, which a caller may then reflect straight
    // back down as a reformatted/blank `value`. Buffer the visible text
    // locally while focused so a re-render like that never overwrites what
    // the user just typed; only round/format once they leave the field,
    // matching how the value gets rounded on submit either way.
    const isBuffered = fractionDigits !== undefined;
    const [isFocused, setIsFocused] = useState(false);
    const [text, setText] = useState(() => toText(value));

    useEffect(() => {
      if (isBuffered && !isFocused) {
        setText(toText(value));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isBuffered]);

    return (
      <InputRoot
        ref={ref}
        onWheel={numberInputOnWheelPreventChange}
        {...props}
        type="number"
        step={
          props.step ??
          (fractionDigits === undefined
            ? undefined
            : fractionDigits === 0
              ? "1"
              : `0.${"0".repeat(fractionDigits - 1)}1`)
        }
        min={props.min ?? 0}
        max={props.max ?? 99999999}
        className={twMerge("w-full", props.className)}
        placeholder={generatePlaceholder(props.placeholder)}
        {...(isBuffered ? { value: text } : { value })}
        onFocus={(event) => {
          if (isBuffered) setIsFocused(true);
          onFocus?.(event);
        }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          if (isBuffered) setText(event.target.value);
          onChange?.(event);
        }}
        onBlur={(event: FocusEvent<HTMLInputElement>) => {
          if (fractionDigits !== undefined) {
            setIsFocused(false);

            const formatted =
              event.target.value === ""
                ? props.required
                  ? formatToFractionDigits("0", fractionDigits)
                  : ""
                : formatToFractionDigits(event.target.value, fractionDigits);

            if (formatted !== event.target.value) {
              event.target.value = formatted;
              setText(formatted);
              onChange?.(event as unknown as ChangeEvent<HTMLInputElement>);
            }
          }

          onBlur?.(event);
        }}
      />
    );
  },
);

InputNumber.displayName = "InputNumber";
